<template>
	<div :class="['progress-bar', ...variantClassList]">
		<div
			class="progress-bar__header"
			data-testid="progress-bar-header"
			v-if="
				$slots.header || headerTitle1 || headerTitle2 || headerWarning
			"
		>
			<span class="progress-bar__header-title-1" v-if="!$slots.header">
				{{ headerTitle1 }}
			</span>
			<span class="progress-bar__header-title-2" v-if="!$slots.header">
				{{ headerTitle2 }}
			</span>
			<span class="progress-bar__header-warning" v-if="!$slots.header">
				{{ headerWarning }}
			</span>
			<slot name="header" />
		</div>
		<div
			class="progress-bar__img"
			v-if="$slots.image"
			data-testid="progress-bar-image"
		>
			<slot name="image" />
		</div>
		<ul
			class="progress-bar__bar"
			:style="widthStyle"
			data-testid="progress-bar-steps"
		>
			<li
				v-for="step in stepArray"
				:key="step.step"
				:class="[
					'progress-bar__bar-section',
					step.complete ? 'is-complete' : 'is-incomplete'
				]"
			></li>
		</ul>
		<div class="progress-bar__messages" data-testid="progress-bar-messages">
			<div class="progress-bar__in-progress-message" v-if="!complete">
				<span
					class="progress-bar__in-progress-title"
					v-if="$slots.inProgressMessage === undefined"
				>
					{{ inProgressTitle }}
				</span>
				<span
					class="progress-bar__in-progress-subtitle"
					v-if="$slots.inProgressMessage === undefined"
				>
					{{ inProgressSubtitle }}
				</span>
				<slot name="inProgressMessage" />
			</div>
			<span
				class="progress-bar__complete-message"
				v-if="complete"
				data-testid="progress-bar-complete"
			>
				<span
					class="progress-bar__complete-title"
					v-if="$slots.completeMessage === undefined"
				>
					{{ completeTitle }}
				</span>
				<slot name="completeMessage" />
			</span>
		</div>
	</div>
</template>

<script>
import baseComponentMixin from '@/mixins/base-component-mixin';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/progress-bar-variant-classes';

export default {
	name: 'ProgressBar',
	mixins: [componentVariantMixin, baseComponentMixin],
	props: {
		stepCount: {
			type: Number,
			default: 0
		},
		currentStep: {
			//Set the current step number that is running
			//Set zero to indicate that all steps are complete
			type: Number,
			default: 0
		},
		complete: {
			type: Boolean,
			default: false
		},
		headerTitle1: {
			type: String,
			default: null
		},
		headerTitle2: {
			type: String,
			default: null
		},
		headerWarning: {
			type: String,
			default: null
		},
		inProgressTitle: {
			type: String,
			default: ''
		},
		inProgressSubtitle: {
			type: String,
			default: ''
		},
		completeTitle: {
			type: String,
			default: 'Complete!'
		},
		width: {
			type: [String, Number],
			default: '450px'
		}
	},
	created() {
		this.variantClasses = variantClasses;
	},
	mounted() {
		console.log(this.$slots.inProgressMessage);
	},
	computed: {
		stepArray() {
			const arr = [];

			for (let index = 1; index <= this.stepCount; index++) {
				const complete =
					index < this.currentStep || this.currentStep == 0;

				arr.push({
					step: index,
					complete: complete,
					stepCount: this.stepCount,
					currentStep: this.currentStep
				});
			}

			return arr;
		},
		widthStyle() {
			if (typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			} else {
				return {
					width: this.width
				};
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.progress-bar {
	max-width: 550px;
	min-width: 340px;
	display: inline-flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background: var(--bg-0);
	color: var(--bg-0-fg-4);

	&__header {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		gap: $gutter-sm;
		margin-bottom: $gutter-lg;
		font-size: $heading-font-size-md;

		> * {
			text-align: center;
		}
	}

	&__img {
		margin-top: $gutter-lg;
		display: inline-flex;
		max-width: 260px;
		width: 260px;
		height: auto;
	}

	&__messages {
		margin-top: $gutter-xl;
		text-align: center;
	}

	&__bar {
		display: flex;
		justify-content: space-between;
		margin-top: $gutter-xl;
		height: 24px;
		border: 2px solid var(--neutral-2);
	}

	&__bar-section {
		flex: 1 1 0;

		&.is-incomplete {
			background: transparent;
		}

		&.is-complete {
			background: var(--brand-2);
		}
	}

	&__in-progress-message {
		display: flex;
		align-items: center;
		flex-direction: column;
	}

	&__in-progress-title {
		margin-bottom: $gutter-md;
	}

	&__in-progress-subtitle {
		font-size: $txt-font-size;
	}

	&__header-warning,
	&__in-progress-title,
	&__complete-title {
		@include heading('md');
	}
}

/* Variant Styles */

.progress-bar--bg-1 {
	background: var(--bg-1);
	color: var(--bg-1-fg-3);
}
.progress-bar--bg-2 {
	background: var(--bg-2);
	color: var(--bg-2-fg-3);
}
</style>
